import { useQuery, useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { sharedMutators } from "../../shared/mutators";
import { queries } from "../../shared/queries";
import { supplierAutoGrantPermissionMessage } from "../../shared/rbac/crossResourceAvailability";
import { formatPermissionMessage } from "../lib/permissionLabels";
import type { GrantRow } from "../../shared/rbac/grantQueries";
import {
  type BuiltinRoleId,
  type GrantEntry,
  grantHasPermission,
  type RoleOption,
  rolesForScope,
  SCOPE_MANAGE_GRANTS_PERMISSION,
} from "../../shared/rbac/roles";
import { defaultRoleForScope } from "../lib/defaultRoleForScope";
import { getWritableScopeIds } from "../lib/getWritableScopeIds";
import { grantEntriesToRows } from "../lib/grantAssembly";
import { deriveScopeGrantDelta } from "../lib/spaceGrantDelta";
import { computeSpaceMemberSupplierGap } from "../lib/spaceMemberSupplierGap";
import {
  grantsWithoutSpacePrincipals,
  removedPrincipalPreviews,
} from "../lib/spaceSupplierAccess";
import { usableSuppliersForSpace } from "../lib/usableSuppliersForSpace";
import { usedSkillsForSpace } from "../lib/usedSuppliersForSpace";
import { MAX_SPACE_NAME_LENGTH, spaceNameError } from "../../shared/spaceName";
import {
  useAffectedChatCount,
  useConnectorDisconnectImpact,
} from "../hooks/useConnectorDisconnectImpact";
import { useCurrentUserGrants } from "../hooks/useCurrentUserGrants";
import { useGrantSpaceAccess } from "../hooks/useGrantSpaceAccess";
import { useGroupMemberships } from "../hooks/useGroupMemberships";
import { useNameField } from "../hooks/useNameField";
import { useScopeGrants } from "../hooks/useScopeGrants";
import { useSpaceDisplayName } from "../hooks/useSpaceDisplayName";
import { useSpaceUsedSuppliers } from "../hooks/useSpaceUsedSuppliers";
import { useSpaces } from "../hooks/useSpaces";
import { PlaceholderAvatar } from "./Avatar";
import { Box } from "./Box";
import { ConfirmModal } from "./ConfirmModal";
import { ConnectorsList } from "./ConnectorsList";
import { DangerSection } from "./Settings/DangerSection";
import { DeleteSpaceModal } from "./DeleteSpaceModal";
import { DisconnectWarning, RemovalDescription } from "./DisconnectWarning";
import { Dropdown } from "./Dropdown";
import { GitSyncForm } from "./GitSyncForm";
import { SpaceGitUrl } from "./SpaceGitUrl";
import { SpaceSlugInput, SpaceUrlField } from "./SpaceUrlField";
import type { PrincipalPreview } from "./PrincipalPreviewList";
import { usePrincipalModals } from "../hooks/usePrincipalModals";
import { Icon } from "./Icon";
import { Input } from "./Input";
import {
  ScopeAccessTable,
  type ScopeAccessPendingHandle,
} from "./ScopeAccessTable";
import { PillBadge } from "./Settings/shared";
import {
  SettingsCard as Card,
  SettingsHeading as SectionHeading,
} from "./SettingsCard";
import { SkillsList } from "./SkillsList";
import { Text } from "./Text";
import { Toggle } from "./Toggle";
import { navigate, routes } from "../lib/urls";

export type SpaceSettingsTab = "settings" | "access";

const TabPanel = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const NameField = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const DiscoverabilityHeader = styled("div")`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 0 8px;
`;

const DiscoverabilityHeaderText = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const JoinerListBox = styled("div")`
  background: var(--surface-primary);
  border: 0.5px solid var(--border-alpha);
  border-radius: 12px;
  overflow: hidden;
`;

const JoinerRow = styled("div")`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 16px;
  height: 64px;
`;

const JoinerInfo = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
`;

function discoverabilityRoleOptions(
  customRoles: readonly {
    id: string;
    name: string;
    scopeKind: string;
    rolePermissions: ReadonlyArray<{ permission: string }>;
  }[]
): RoleOption[] {
  return rolesForScope("space", [...customRoles]).filter((opt) => {
    if (opt.builtinRoleId) {
      return !grantHasPermission(
        {
          builtinRoleId: opt.builtinRoleId,
          customRole: null,
        },
        "space:manage"
      );
    }
    if (opt.customRoleId) {
      const role = customRoles.find((r) => r.id === opt.customRoleId);
      if (!role) return false;
      return !role.rolePermissions.some(
        (p) => p.permission === "space:manage" || p.permission === "allow_all"
      );
    }
    return false;
  });
}

function roleOptionLabel(role: RoleOption): string {
  return role.label;
}

function currentRoleOption(
  options: RoleOption[],
  builtinRoleId: string | null | undefined,
  customRoleId: string | null | undefined
): RoleOption | undefined {
  if (builtinRoleId) {
    return options.find((o) => o.builtinRoleId === builtinRoleId);
  }
  if (customRoleId) {
    return options.find((o) => o.customRoleId === customRoleId);
  }
  return undefined;
}

type StagedGrantsRef = { current: ScopeAccessPendingHandle | null };

type EditProps = {
  mode: "edit";
  spaceId: string;
  canManageSpace: boolean;
  activeTab: SpaceSettingsTab;
  stagedGrantsRef?: StagedGrantsRef;
  onDeleted?: () => void;
  accessMapPanel?: ReactNode;
};

type CreateProps = {
  mode: "create";
  name: string;
  nameError: string | null;
  setName: (name: string) => void;
  slug: string;
  setSlug: (slug: string) => void;
  slugError: string | null;
  slugTaken: boolean;
  grants: GrantEntry[];
  setGrants: (grants: GrantEntry[]) => void;
  discoverability: DiscoverabilityValue;
  setDiscoverability: (value: DiscoverabilityValue) => void;
  activeTab: SpaceSettingsTab;
  stagedGrantsRef?: StagedGrantsRef;
};

type Props = EditProps | CreateProps;

function SpaceSettingsFormInner({
  spaceId,
  canManageSpace: canManageSpaceProp,
  activeTab,
  stagedGrantsRef,
  onDeleted,
  accessMapPanel,
}: {
  spaceId: string;
  canManageSpace: boolean;
  activeTab: SpaceSettingsTab;
  stagedGrantsRef?: StagedGrantsRef;
  onDeleted?: () => void;
  accessMapPanel?: ReactNode;
}) {
  const z = useZero();
  const displaySpaceName = useSpaceDisplayName();
  const { onOpenUser, onOpenGroup, onOpenServiceAccount } =
    usePrincipalModals();
  const [currentUser, currentUserResult] = useQuery(queries.currentUser());
  const [space, spaceResult] = useQuery(queries.navSpace({ spaceId }));
  const [spaceGrants, grantsResult] = useQuery(
    queries.roleGrantsByScope({ scopeId: spaceId })
  );
  const isPersonalSpace = space?.mode === "personal";
  const canManageSpace = canManageSpaceProp && !isPersonalSpace;
  const [allSpaces] = useSpaces({ settingsView: false });
  const [allApps, appsResult] = useQuery(queries.connectorsWithUsages());
  const [allUserGroups, groupsResult] = useQuery(
    queries.selectableUserGroups()
  );
  const [allUsers, usersResult] = useQuery(queries.allUsers());
  const [allServiceAccounts] = useQuery(queries.serviceAccounts());
  const [customRoles, rolesResult] = useQuery(queries.allRolesLite());
  const allGroups = allUserGroups;
  const nameInputRef = useRef<HTMLInputElement>(null);

  const nameField = useNameField({
    currentName: space?.name,
    validate: (name) => spaceNameError(name, allSpaces, space),
    onCommit: (name: string) => {
      z.mutate(sharedMutators.updateSpace({ spaceId, name }));
    },
  });

  const {
    allGrants,
    assignedGroups,
    assignedUsers,
    roleOptions: allRoleOptions,
    commitGrants,
    removeGroup,
    removeUser,
    managerConfirmModal,
  } = useScopeGrants({
    scopeId: spaceId,
    scopeKind: "space",
    rawGrants: spaceGrants,
    ready: [
      currentUserResult,
      spaceResult,
      grantsResult,
      groupsResult,
      rolesResult,
    ].every((result) => result.type === "complete"),
    allGroups,
    allUsers,
    customRoles,
    viewerId: currentUser?.id,
    resourceLabel: "space",
  });

  const { userGroupIds: userGroupMemberships } = useGroupMemberships();

  const usableApps = useMemo(
    () =>
      usableSuppliersForSpace(
        "space_uses_connected_apps",
        spaceGrants,
        allApps,
        userGroupMemberships
      ),
    [allApps, spaceGrants, userGroupMemberships]
  );
  const { usedConnectors: usedApps, usedConnectorsLoaded } =
    useSpaceUsedSuppliers(allApps, spaceId);

  const [myGrants, myGrantsResult] = useCurrentUserGrants();
  const manageableConnectorIds = useMemo(
    () =>
      getWritableScopeIds(
        myGrants,
        allApps.map((a) => a.id),
        SCOPE_MANAGE_GRANTS_PERMISSION.connected_app
      ),
    [myGrants, allApps]
  );
  const removableAppIds = useMemo(
    () =>
      new Set(
        usableApps
          .filter(
            (app) =>
              manageableConnectorIds.has(app.id) &&
              grantsWithoutSpacePrincipals(app.roleGrantsAsScope, spaceGrants)
                .removed.length > 0
          )
          .map((a) => a.id)
      ),
    [manageableConnectorIds, usableApps, spaceGrants]
  );

  const [allSkills, skillsResult] = useQuery(queries.skillsIndex());
  const availabilityReady = [spaceResult, grantsResult, groupsResult].every(
    (result) => result.type === "complete"
  );
  const connectorsReady = availabilityReady && appsResult.type === "complete";
  const skillsReady = availabilityReady && skillsResult.type === "complete";
  const removalReady = [
    currentUserResult,
    spaceResult,
    grantsResult,
    groupsResult,
    rolesResult,
    myGrantsResult,
  ].every((result) => result.type === "complete");
  const selectionReady =
    removalReady &&
    usedConnectorsLoaded &&
    [appsResult, skillsResult, usersResult].every(
      (result) => result.type === "complete"
    );
  const { guard: guardSpaceAccess, modal: grantAccessModal } =
    useGrantSpaceAccess({ ready: selectionReady });

  const usableSkills = useMemo(
    () =>
      usableSuppliersForSpace(
        "space_uses_skills",
        spaceGrants,
        allSkills,
        userGroupMemberships
      ),
    [allSkills, spaceGrants, userGroupMemberships]
  );
  const usedSkills = useMemo(
    () => usedSkillsForSpace(spaceGrants, allSkills),
    [spaceGrants, allSkills]
  );
  const manageableSkillIds = useMemo(
    () =>
      getWritableScopeIds(
        myGrants,
        allSkills.map((s) => s.id),
        SCOPE_MANAGE_GRANTS_PERMISSION.skill
      ),
    [myGrants, allSkills]
  );

  const removableSkillIds = useMemo(
    () =>
      new Set(
        usableSkills
          .filter(
            (skill) =>
              manageableSkillIds.has(skill.id) &&
              grantsWithoutSpacePrincipals(skill.roleGrantsAsScope, spaceGrants)
                .removed.length > 0
          )
          .map((s) => s.id)
      ),
    [manageableSkillIds, usableSkills, spaceGrants]
  );

  const [removingScope, setRemovingScope] = useState<{
    scopeKind: "connected_app" | "skill";
    id: string;
  } | null>(null);
  const removingSupplier = removingScope
    ? removingScope.scopeKind === "connected_app"
      ? (usableApps.find((a) => a.id === removingScope.id) ?? null)
      : (usableSkills.find((s) => s.id === removingScope.id) ?? null)
    : null;
  const removingName =
    removingSupplier?.name ||
    (removingScope?.scopeKind === "skill" ? "this skill" : "this connector");
  const supplierGrantsReady =
    removalReady &&
    (removingScope?.scopeKind === "connected_app"
      ? connectorsReady
      : skillsReady);
  const [computeRemovalImpact, computeRemovalImpactLoaded] =
    useConnectorDisconnectImpact(
      removingScope?.scopeKind === "connected_app" ? removingScope.id : null
    );
  const removalNext = useMemo(() => {
    if (!removingSupplier) return [];
    const entries: GrantEntry[] = removingSupplier.roleGrantsAsScope
      .filter((g) => g.userGroupId || g.userId || g.serviceAccountId)
      .map((g) => ({
        userGroupId: g.userGroupId ?? undefined,
        userId: g.userId ?? undefined,
        serviceAccountId: g.serviceAccountId ?? undefined,
        builtinRoleId: g.builtinRoleId ?? undefined,
        customRoleId: g.customRoleId ?? undefined,
      }));
    return grantsWithoutSpacePrincipals(entries, spaceGrants).next;
  }, [removingSupplier, spaceGrants]);
  const removalImpact =
    removingScope?.scopeKind === "connected_app" && removingSupplier
      ? computeRemovalImpact(removingSupplier.roleGrantsAsScope, removalNext)
      : null;
  const [chatCount, chatCountLoaded] = useAffectedChatCount(
    removingScope?.scopeKind === "connected_app" ? removingScope.id : null,
    removalImpact?.affectedUserIds ?? []
  );
  const removedPrincipals: PrincipalPreview[] = useMemo(() => {
    if (!removingSupplier) return [];
    const { removed } = grantsWithoutSpacePrincipals(
      removingSupplier.roleGrantsAsScope,
      spaceGrants
    );
    return removedPrincipalPreviews(removed, {
      groupById: new Map(allGroups.map((g) => [g.id, g] as const)),
      userById: new Map(
        allUsers.flatMap((u) => (u ? [[u.id, u] as const] : []))
      ),
      serviceAccountById: new Map(
        allServiceAccounts.map((sa) => [sa.id, sa] as const)
      ),
    });
  }, [removingSupplier, spaceGrants, allGroups, allUsers, allServiceAccounts]);

  const supplierRemovalReady =
    supplierGrantsReady && computeRemovalImpactLoaded && chatCountLoaded;
  const confirmRemoveSupplier = useCallback(() => {
    if (!supplierRemovalReady || !removingScope || !removingSupplier) return;
    const { add, remove } = deriveScopeGrantDelta(
      removingSupplier.roleGrantsAsScope,
      removalNext
    );
    z.mutate(
      sharedMutators.patchScopeGrants({
        scopeId: removingScope.id,
        scopeKind: removingScope.scopeKind,
        add,
        remove,
      })
    );
    setRemovingScope(null);
  }, [z, removingScope, removingSupplier, removalNext, supplierRemovalReady]);

  const membershipCommitState = (desired: GrantEntry[]) => {
    if (
      !selectionReady ||
      deriveScopeGrantDelta(spaceGrants, desired).add.length === 0
    )
      return { gaps: [] };
    const gaps = computeSpaceMemberSupplierGap({
      currentSpaceGrants: spaceGrants,
      desiredSpaceGrants: grantEntriesToRows(desired, customRoles),
      connectors: usedApps,
      skills: usedSkills,
      memberships: userGroupMemberships,
      manageableConnectorIds,
      manageableSkillIds,
    });

    const blocked = gaps.filter((gap) => !gap.canShare);
    if (blocked.length > 0)
      return {
        gaps,
        blockedReason: blocked
          .map((gap) =>
            formatPermissionMessage(
              supplierAutoGrantPermissionMessage(
                gap.supplierName,
                SCOPE_MANAGE_GRANTS_PERMISSION[gap.scopeKind]
              )
            )
          )
          .join(". "),
      };
    return { gaps };
  };

  const preflightCommitGrants = (
    desired: GrantEntry[],
    onCommitted?: () => void
  ) => {
    if (!canManageSpace) return false;
    if (
      !selectionReady &&
      deriveScopeGrantDelta(spaceGrants, desired).add.length > 0
    )
      return false;
    const { blockedReason, gaps } = membershipCommitState(desired);
    if (blockedReason) return false;
    if (gaps.length === 0) {
      commitGrants(desired);
      return undefined;
    }
    // Deferred: keep the member staged behind the grant modal, and add them
    // (then run the caller's continuation, e.g. close) only once it confirms.
    guardSpaceAccess({
      spaceName: space?.name ?? "this space",
      gaps,
      proceed: () => {
        commitGrants(desired);
        onCommitted?.();
      },
    });
    return false;
  };

  if (!space) {
    return null;
  }

  if (isPersonalSpace) return null;

  return (
    <>
      {managerConfirmModal}
      {grantAccessModal}

      <TabPanel style={{ display: activeTab === "settings" ? "flex" : "none" }}>
        <Card style={{ gap: 24 }}>
          <NameField>
            <Box horizontal={8}>
              <Text variant="text-small-md">Space name</Text>
            </Box>
            <Input
              ref={nameInputRef}
              value={nameField.value}
              onChange={(e) => nameField.setValue(e.target.value)}
              onBlur={nameField.commit}
              onKeyDown={(e) => {
                if (e.key === "Enter") nameInputRef.current?.blur();
              }}
              disabled={!canManageSpace}
              maxLength={MAX_SPACE_NAME_LENGTH}
              style={{ width: "100%" }}
            />
            {nameField.error && (
              <Box horizontal={8}>
                <Text variant="text-small" color="var(--text-danger)">
                  {nameField.error}
                </Text>
              </Box>
            )}
          </NameField>
          <SpaceUrlField space={space} canManageSpace={canManageSpace} />
        </Card>

        <DiscoverabilitySection
          value={{
            defaultJoinBuiltinRoleId: space.defaultJoinBuiltinRoleId ?? null,
            defaultJoinCustomRoleId: space.defaultJoinCustomRoleId ?? null,
          }}
          onChange={(next) =>
            z.mutate(
              sharedMutators.updateSpace({
                spaceId,
                defaultJoinBuiltinRoleId: next.defaultJoinBuiltinRoleId,
                defaultJoinCustomRoleId: next.defaultJoinCustomRoleId,
              })
            )
          }
          customRoles={customRoles}
          canManage={canManageSpace}
        />

        <SpaceGitUrl slug={space.slug} />

        <Card>
          <DiscoverabilityHeader>
            <DiscoverabilityHeaderText>
              <Text variant="text-regular-md">Peer-reviewed push live</Text>
              <Text variant="text-small" color="var(--text-secondary)">
                Members can’t push a branch live if they worked on it — a peer
                with push access has to do it.
              </Text>
            </DiscoverabilityHeaderText>
            <Toggle
              isOn={space.blockSelfApproval ?? false}
              onToggle={() =>
                z.mutate(
                  sharedMutators.updateSpace({
                    spaceId,
                    blockSelfApproval: !space.blockSelfApproval,
                  })
                )
              }
              disabled={!canManageSpace}
            />
          </DiscoverabilityHeader>
        </Card>

        <GitSyncForm spaceId={spaceId} canManageSpace={canManageSpace} />

        {canManageSpace && (
          <DangerSection
            title="Delete space"
            description="Permanently deletes this space and everything in it — workflows, steps, and runs."
            actionLabel="Delete space"
            renderConfirm={({ open, onClose }) =>
              open ? (
                <DeleteSpaceModal
                  spaceId={spaceId}
                  spaceName={displaySpaceName(space)}
                  onClose={onClose}
                  onDeleted={onDeleted}
                />
              ) : null
            }
          />
        )}
      </TabPanel>

      <TabPanel style={{ display: activeTab === "access" ? "flex" : "none" }}>
        {accessMapPanel}
        <Card>
          <SectionHeading>
            <Text variant="text-regular-md">Access</Text>
            <Text variant="text-small" color="var(--text-secondary)">
              Add members and groups, and choose their role.
            </Text>
          </SectionHeading>
          <ScopeAccessTable
            allGrants={allGrants}
            assignedGroups={assignedGroups}
            assignedUsers={assignedUsers}
            roleOptions={allRoleOptions}
            allGroups={allGroups}
            allUsers={allUsers}
            customRoles={customRoles}
            canManage={canManageSpace}
            selectionReady={selectionReady}
            removalReady={removalReady}
            getCommitState={membershipCommitState}
            compact
            commitGrants={preflightCommitGrants}
            removeGroup={removeGroup}
            removeUser={removeUser}
            onOpenUser={onOpenUser}
            onOpenGroup={onOpenGroup}
            onOpenServiceAccount={onOpenServiceAccount}
            includeServiceAccounts
            stagedGrantsRef={stagedGrantsRef}
            defaultRole={defaultRoleForScope("space", allRoleOptions)}
          />
        </Card>

        <Card>
          <SectionHeading>
            <Text variant="text-regular-md">Connectors</Text>
            <Text variant="text-small" color="var(--text-secondary)">
              Connectors members can use in workflows here.
            </Text>
          </SectionHeading>
          <ConnectorsList
            apps={usableApps}
            disabled={!connectorsReady}
            removalReady={removalReady}
            onSelect={(app) => navigate(routes.modal.connector, { id: app.id })}
            emptyDescription="Connectors appear here when every member with space access can also use them."
            removableIds={removableAppIds}
            onRemove={(app) =>
              setRemovingScope({ scopeKind: "connected_app", id: app.id })
            }
          />
        </Card>

        <Card>
          <SectionHeading>
            <Text variant="text-regular-md">Skills</Text>
            <Text variant="text-small" color="var(--text-secondary)">
              Skills members can use here.
            </Text>
          </SectionHeading>
          <SkillsList
            skills={usableSkills}
            disabled={!skillsReady}
            removalReady={removalReady}
            onSelect={(skill) => navigate(routes.modal.skill, { id: skill.id })}
            emptyDescription="Skills appear here when every member with space access can also use them."
            removableIds={removableSkillIds}
            onRemove={(skill) =>
              setRemovingScope({ scopeKind: "skill", id: skill.id })
            }
          />
        </Card>
      </TabPanel>

      <ConfirmModal
        open={removingSupplier !== null}
        onClose={() => setRemovingScope(null)}
        onConfirm={confirmRemoveSupplier}
        title={`Remove ${removingName}?`}
        description={
          <RemovalDescription
            target={removingName}
            principals={removedPrincipals}
            workflows={removalImpact?.workflows.length ?? 0}
          />
        }
        confirmLabel="Remove"
        confirmDisabled={!supplierRemovalReady}
        destructive
      >
        <DisconnectWarning
          principals={removedPrincipals}
          workflows={removalImpact?.workflows ?? []}
          gitSpaceNames={removalImpact?.gitSpaceNames ?? []}
          chatCount={chatCount}
        />
      </ConfirmModal>
    </>
  );
}

function CreateSpaceSettingsForm({
  name,
  nameError,
  setName,
  slug,
  setSlug,
  slugError,
  slugTaken,
  grants,
  setGrants,
  discoverability,
  setDiscoverability,
  activeTab,
  stagedGrantsRef,
}: Omit<CreateProps, "mode">) {
  const { onOpenUser, onOpenGroup, onOpenServiceAccount } =
    usePrincipalModals();
  const nameRef = useRef<HTMLInputElement>(null);

  // The dialog grabs focus on open (Modal calls el.focus()), so re-focus the
  // name field on the next frame so the user can start typing immediately.
  useEffect(() => {
    const id = requestAnimationFrame(() => nameRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, []);

  const [allApps, appsResult] = useQuery(queries.connectorsIndex());
  const [allUserGroups, groupsResult] = useQuery(
    queries.selectableUserGroups()
  );
  const [allUsers, usersResult] = useQuery(queries.allUsers());
  const [customRoles, rolesResult] = useQuery(queries.allRolesLite());
  const allGroups = allUserGroups;

  const {
    allGrants,
    assignedGroups,
    assignedUsers,
    roleOptions: allRoleOptions,
    commitGrants,
    removeGroup,
    removeUser,
  } = useScopeGrants({
    scopeKind: "space",
    grants,
    onCommit: setGrants,
    allGroups,
    allUsers,
    customRoles,
  });

  const { userGroupIds: userGroupMemberships } = useGroupMemberships();

  const spaceGrantRows: GrantRow[] = useMemo(() => {
    const roleMap = new Map(customRoles.map((r) => [r.id, r]));
    return allGrants.map((g) => ({
      userGroupId: g.userGroupId ?? null,
      userId: g.userId ?? null,
      builtinRoleId: g.builtinRoleId ?? null,
      customRole: g.customRoleId ? (roleMap.get(g.customRoleId) ?? null) : null,
    }));
  }, [allGrants, customRoles]);

  const usableApps = useMemo(
    () =>
      usableSuppliersForSpace(
        "space_uses_connected_apps",
        spaceGrantRows,
        allApps,
        userGroupMemberships
      ),
    [allApps, spaceGrantRows, userGroupMemberships]
  );

  const [allSkills, skillsResult] = useQuery(queries.skillsIndex());
  const availabilityReady = [groupsResult, rolesResult].every(
    (result) => result.type === "complete"
  );
  const usableSkills = useMemo(
    () =>
      usableSuppliersForSpace(
        "space_uses_skills",
        spaceGrantRows,
        allSkills,
        userGroupMemberships
      ),
    [allSkills, spaceGrantRows, userGroupMemberships]
  );

  return (
    <>
      <TabPanel style={{ display: activeTab === "settings" ? "flex" : "none" }}>
        <Card style={{ gap: 24 }}>
          <NameField>
            <Box horizontal={8}>
              <Text variant="text-small-md">Space name</Text>
            </Box>
            <Input
              ref={nameRef}
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Space name"
              maxLength={MAX_SPACE_NAME_LENGTH}
              style={{ width: "100%" }}
            />
            {nameError && (
              <Box horizontal={8}>
                <Text variant="text-small" color="var(--text-danger)">
                  {nameError}
                </Text>
              </Box>
            )}
          </NameField>
          <NameField>
            <Box horizontal={8}>
              <Text variant="text-small-md">Space URL</Text>
            </Box>
            <SpaceSlugInput
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="space-url"
            />
            <Box horizontal={8}>
              {slugError ? (
                <Text variant="text-small" color="var(--text-danger)">
                  {slugError}
                </Text>
              ) : slugTaken ? (
                <Text variant="text-small" color="var(--text-danger)">
                  This space URL is already taken.
                </Text>
              ) : (
                <Text variant="text-small" color="var(--text-secondary)">
                  Your workflows’ links will live at this address.
                </Text>
              )}
            </Box>
          </NameField>
        </Card>

        <DiscoverabilitySection
          value={discoverability}
          onChange={setDiscoverability}
          customRoles={customRoles}
          canManage
        />
      </TabPanel>

      <TabPanel style={{ display: activeTab === "access" ? "flex" : "none" }}>
        <Card>
          <SectionHeading>
            <Text variant="text-regular-md">Access</Text>
            <Text variant="text-small" color="var(--text-secondary)">
              Add members and groups, and choose their role.
            </Text>
          </SectionHeading>
          <ScopeAccessTable
            allGrants={allGrants}
            assignedGroups={assignedGroups}
            assignedUsers={assignedUsers}
            roleOptions={allRoleOptions}
            allGroups={allGroups}
            allUsers={allUsers}
            customRoles={customRoles}
            canManage
            selectionReady={[groupsResult, usersResult, rolesResult].every(
              (result) => result.type === "complete"
            )}
            compact
            commitGrants={commitGrants}
            removeGroup={removeGroup}
            removeUser={removeUser}
            onOpenUser={onOpenUser}
            onOpenGroup={onOpenGroup}
            onOpenServiceAccount={onOpenServiceAccount}
            includeServiceAccounts
            stagedGrantsRef={stagedGrantsRef}
            defaultRole={defaultRoleForScope("space", allRoleOptions)}
          />
        </Card>

        <Card>
          <SectionHeading>
            <Text variant="text-regular-md">Connectors</Text>
            <Text variant="text-small" color="var(--text-secondary)">
              Connectors members can use in workflows here.
            </Text>
          </SectionHeading>
          <ConnectorsList
            apps={usableApps}
            disabled={!availabilityReady || appsResult.type !== "complete"}
            onSelect={(app) => navigate(routes.modal.connector, { id: app.id })}
            emptyDescription="Connectors appear here when every member with space access can also use them."
          />
        </Card>

        <Card>
          <SectionHeading>
            <Text variant="text-regular-md">Skills</Text>
            <Text variant="text-small" color="var(--text-secondary)">
              Skills members can use here.
            </Text>
          </SectionHeading>
          <SkillsList
            skills={usableSkills}
            disabled={!availabilityReady || skillsResult.type !== "complete"}
            onSelect={(skill) => navigate(routes.modal.skill, { id: skill.id })}
            emptyDescription="Skills appear here when every member with space access can also use them."
          />
        </Card>
      </TabPanel>
    </>
  );
}

export type DiscoverabilityValue = {
  defaultJoinBuiltinRoleId: BuiltinRoleId | null;
  defaultJoinCustomRoleId: string | null;
};

function DiscoverabilitySection({
  value,
  onChange,
  customRoles,
  canManage,
}: {
  value: DiscoverabilityValue;
  onChange: (value: DiscoverabilityValue) => void;
  customRoles: readonly {
    id: string;
    name: string;
    scopeKind: string;
    rolePermissions: ReadonlyArray<{ permission: string }>;
  }[];
  canManage: boolean;
}) {
  const { defaultJoinBuiltinRoleId, defaultJoinCustomRoleId } = value;
  const discoverable = Boolean(
    defaultJoinBuiltinRoleId || defaultJoinCustomRoleId
  );
  const roleOptions = useMemo(
    () => discoverabilityRoleOptions(customRoles),
    [customRoles]
  );
  const selectedRole = currentRoleOption(
    roleOptions,
    defaultJoinBuiltinRoleId,
    defaultJoinCustomRoleId
  );

  const fallbackBuiltinId = "space_viewer";
  const fallbackOption = roleOptions.find(
    (o) => o.builtinRoleId === fallbackBuiltinId
  );

  const setRole = (role: RoleOption | null) => {
    onChange({
      defaultJoinBuiltinRoleId: role?.builtinRoleId ?? null,
      defaultJoinCustomRoleId: role?.customRoleId ?? null,
    });
  };

  const onToggle = () => {
    if (!canManage) return;
    if (discoverable) {
      setRole(null);
    } else if (fallbackOption) {
      setRole(fallbackOption);
    }
  };

  return (
    <Card>
      <DiscoverabilityHeader>
        <DiscoverabilityHeaderText>
          <Text variant="text-regular-md">Discoverability</Text>
          <Text variant="text-small" color="var(--text-secondary)">
            Lets anyone in your tenant find and join this space themselves, with
            the role below.
          </Text>
        </DiscoverabilityHeaderText>
        <Toggle isOn={discoverable} onToggle={onToggle} disabled={!canManage} />
      </DiscoverabilityHeader>
      {discoverable && (
        <JoinerListBox>
          <JoinerRow>
            <PlaceholderAvatar size={32} />
            <JoinerInfo>
              <Text variant="text-regular-md">Anyone in your tenant</Text>
              <Text variant="text-small" color="var(--text-secondary)">
                Joins as
              </Text>
            </JoinerInfo>
            <Dropdown
              align="bottom-left"
              opener={
                <PillBadge icon="id-card-lanyard" interactive={canManage}>
                  {selectedRole ? roleOptionLabel(selectedRole) : "Pick a role"}
                </PillBadge>
              }
              sections={[
                {
                  items: roleOptions.map((role) => ({
                    type: "button" as const,
                    key: role.builtinRoleId ?? role.customRoleId ?? role.label,
                    left: <Icon name="id-card-lanyard" size={16} />,
                    label: role.label,
                    selected:
                      selectedRole?.builtinRoleId === role.builtinRoleId &&
                      selectedRole?.customRoleId === role.customRoleId,
                    onClick: () => {
                      if (!canManage) return;
                      setRole(role);
                    },
                  })),
                },
              ]}
            />
          </JoinerRow>
        </JoinerListBox>
      )}
    </Card>
  );
}

export function SpaceSettingsForm(props: Props) {
  if (props.mode === "create") {
    return (
      <CreateSpaceSettingsForm
        name={props.name}
        nameError={props.nameError}
        setName={props.setName}
        slug={props.slug}
        setSlug={props.setSlug}
        slugError={props.slugError}
        slugTaken={props.slugTaken}
        grants={props.grants}
        setGrants={props.setGrants}
        discoverability={props.discoverability}
        setDiscoverability={props.setDiscoverability}
        activeTab={props.activeTab}
        stagedGrantsRef={props.stagedGrantsRef}
      />
    );
  }
  return (
    <SpaceSettingsFormInner
      spaceId={props.spaceId}
      canManageSpace={props.canManageSpace}
      activeTab={props.activeTab}
      stagedGrantsRef={props.stagedGrantsRef}
      onDeleted={props.onDeleted}
      accessMapPanel={props.accessMapPanel}
    />
  );
}
